<script setup lang="ts">
/**
 * Everything that has to happen ABOVE the i18n provider, because the provider
 * needs the locale and the locale is negotiated against the brand's configuration.
 *
 *   host link  — the handshake, and therefore the page origin, and therefore the
 *                first request this app is allowed to make
 *   theme      — applied to <html>, since the host page owns the appearance;
 *                under `auto` that includes the page's own colours (ADR-0248)
 *   session    — useWidget(), which resolves the locale the provider then uses
 */
import { computed, onBeforeUnmount, ref, shallowRef, toRef, watch, watchEffect } from 'vue';

import { provideWidgetI18n } from '../vendor/i18n';
import Panel from '../screens/Panel.vue';
import type { HostPalette } from '../loader/protocol';
import { accentColor } from '../theme/appearance';
import {
  applyPalette,
  launcherColour,
  paletteVariables,
  parseColour,
  readSiteColours,
} from '../theme/host-palette';
import { applyTheme, DARK_MEDIA_QUERY, type ThemePreference } from '../theme/theme';

import type { BootParams } from './boot';
import { createHostLink } from './host';
import { useWidget } from './useWidget';

const props = defineProps<{ readonly params: BootParams }>();

const themePreference = ref<ThemePreference>(props.params.theme);
const darkQuery = window.matchMedia(DARK_MEDIA_QUERY);
const prefersDark = ref(darkQuery.matches);
/** The host page's colours from the handshake; null until then, and from an old loader. */
const hostPalette = shallowRef<HostPalette | null>(null);

/**
 * `null` means "the handshake has not happened yet" and blocks the first API
 * call. A frame nobody is framing — `pnpm dev` opened directly — never gets a
 * handshake, so it uses its own origin: it is genuinely the top-level page
 * there, and the API will reject it unless the brand's dev-mode window is open,
 * which is exactly the behaviour that window exists for.
 */
const pageOrigin = ref<string | null>(window.parent === window ? window.location.origin : null);
const open = ref(false);

/**
 * Created in setup rather than on mount: the loader may post its handshake the
 * moment the frame's document exists, and the listener this installs is the
 * only thing that can hear it.
 */
const link = createHostLink({
  onInit: (init) => {
    // BEFORE the origin: setting `pageOrigin` releases the first bootstrap, and
    // a site that called `identify` before the handshake wants that first
    // bootstrap to be the player's (ADR-0249 §4), not an anonymous one followed
    // by a second. `init` is re-sent until `ready`; `host.ts` passes only the
    // first one here, so this answers once.
    if (init.identity !== null) {
      const { jwt, n } = init.identity;
      void view.identify(jwt).then((outcome) => {
        if (n !== null) {
          link.done(n, outcome);
        }
      });
    }
    pageOrigin.value = init.origin;
    themePreference.value = init.theme;
    hostPalette.value = init.palette;
    // The locale from the host page was already in the frame URL and is
    // handled by readBootParams; repeating it here would give two sources of
    // truth for the first paint.
  },
  onVisibility: (visible) => {
    open.value = visible;
  },
  onTheme: (theme) => {
    themePreference.value = theme;
  },
  onIdentify: (jwt, n) => {
    void view.identify(jwt).then((outcome) => {
      link.done(n, outcome);
    });
  },
  onLogout: (n) => {
    void view.logout().then((outcome) => {
      link.done(n, outcome);
    });
  },
});

const onSchemeChange = (event: MediaQueryListEvent): void => {
  prefersDark.value = event.matches;
};
darkQuery.addEventListener('change', onSchemeChange);

onBeforeUnmount(() => {
  darkQuery.removeEventListener('change', onSchemeChange);
  link.dispose();
});

const site = computed(() => readSiteColours(hostPalette.value));

const view = useWidget({
  publicKey: props.params.publicKey,
  requestedLocale: props.params.requestedLocale,
  pageOrigin,
  open,
});

provideWidgetI18n(toRef(view, 'locale'));

/** A colour the brand set itself (`appearance.theme`), when it set one. */
const brandAccent = computed(() => accentColor(view.appearance));

/**
 * The panel's colours. The brand's own accent outranks the site's: someone chose
 * it on purpose, while the site's is a guess from its buttons. The page's
 * surfaces are worn only under `auto` — `light`, `dark` and `system` are a site
 * asking for our look.
 */
watchEffect(() => {
  const root = document.documentElement;
  const scheme = applyTheme(root, themePreference.value, prefersDark.value, site.value.pageIsDark);
  applyPalette(
    root.style,
    paletteVariables({
      accent: parseColour(brandAccent.value) ?? site.value.accent,
      page: themePreference.value === 'auto' ? site.value.page : null,
      scheme,
    }),
  );
});

/**
 * The launcher on the host page cannot read our tokens, so it is told its colour:
 * the brand's verbatim, as before, or else the site's, darkened if the white
 * glyph would not read on it.
 */
const launcherAccent = computed(() => {
  if (brandAccent.value !== null) {
    return brandAccent.value;
  }
  const accent = site.value.accent;
  return accent === null ? null : launcherColour(accent);
});
const booted = computed(() => view.phase.kind === 'ready' || view.phase.kind === 'prechat');

watch(
  [booted, launcherAccent],
  ([isBooted, colour]) => {
    if (isBooted) {
      link.ready(colour);
    } else if (colour !== null) {
      // Known from the handshake alone, so the button takes the site's colour
      // without waiting for the session call.
      link.accent(colour);
    }
  },
  { immediate: true },
);

watch(
  () => view.unread,
  (count) => {
    link.unread(count);
  },
  { immediate: true },
);

/**
 * `<html lang>` is set before first paint by the inline script from the frame
 * URL; this keeps it honest after the locale is renegotiated against the
 * brand's staffed languages. Screen-reader pronunciation depends on it.
 *
 * Registered AFTER `provideWidgetI18n`, which also writes `lang` (as a full
 * BCP 47 tag), so this one runs second and wins — the same order, and the
 * same final value, as the React tree it replaces, where the root's effect ran
 * after its provider child's.
 */
watch(
  () => view.locale,
  (next) => {
    document.documentElement.lang = next;
  },
  { immediate: true },
);

/**
 * The host page owns the panel's visibility — it is the one drawing it — so
 * closing is a REQUEST, not an assignment. The `close` message comes back down
 * as `onVisibility(false)` and that is what flips the local state.
 */
function requestClose(): void {
  link.requestClose();
}

function reportLabel(text: string): void {
  link.label(text);
}
</script>

<template>
  <Panel :view="view" :open="open" @request-close="requestClose" @label="reportLabel" />
</template>
